import {
  CompatibleProductId,
  CompatibleProductName,
  HostingDisplayName,
  HostingTypesWithCloudComplianceBoundaries,
} from '@atlassian/mpac-types';

import administrativeTools from '../../../assets/images/administrative-tools.svg';
import bamboo from '../../../assets/images/bamboo.svg';
import bitbucket from '../../../assets/images/bitbucket.svg';
import compass from '../../../assets/images/compass.svg';
import confluence from '../../../assets/images/confluence.svg';
import contentAndCommunication from '../../../assets/images/content-and-communication.svg';
import crowd from '../../../assets/images/crowd.svg';
import crucible from '../../../assets/images/crucible.svg';
import dataAndAnalytics from '../../../assets/images/data-and-analytics.svg';
import designAndDiagramming from '../../../assets/images/design-and-diagramming.svg';
import fisheye from '../../../assets/images/fisheye.svg';
import governmentCloud from '../../../assets/images/government-cloud.svg';
import hrAndTeamBuilding from '../../../assets/images/hr-and-team-wellbeing.svg';
import isolatedCloud from '../../../assets/images/isolated-cloud.svg';
import itSupportAndService from '../../../assets/images/it-support-and-service.svg';
import jiraServiceManagement from '../../../assets/images/jira-service-management.svg';
import jiraSoftware from '../../../assets/images/jira-software.svg';
import projectManagement from '../../../assets/images/project-Management.svg';
import salesAndCustomerRelations from '../../../assets/images/sales-and-customer-relations.svg';
import securityAndCompliance from '../../../assets/images/security-and-compliance.svg';
import softwareDevelopment from '../../../assets/images/software-development.svg';

export const products = [
  {
    id: CompatibleProductId.Jira,
    name: CompatibleProductName[CompatibleProductId.Jira],
    icon: jiraSoftware,
    description: 'Power up Jira with apps for project tracking, automation, and reporting.',
  },
  {
    id: CompatibleProductId.Confluence,
    name: CompatibleProductName[CompatibleProductId.Confluence],
    icon: confluence,
    description: 'Expand Confluence with tools for team knowledge sharing and documentation.',
  },
  {
    id: CompatibleProductId.JiraServiceManagement,
    name: CompatibleProductName[CompatibleProductId.JiraServiceManagement],
    icon: jiraServiceManagement,
    description: 'Extend Jira Service Management with apps for support, automation, and ITSM.',
  },
  {
    id: CompatibleProductId.COMPASS,
    name: CompatibleProductName[CompatibleProductId.COMPASS],
    icon: compass,
    description: 'Extend Compass with tools for cataloging and governing software architecture.',
  },
  {
    id: CompatibleProductId.Bitbucket,
    name: CompatibleProductName[CompatibleProductId.Bitbucket],
    icon: bitbucket,
    description:
      'Apps to broaden the reach and power of Bitbucket and augment the way you collaborate on code.',
  },
  {
    id: CompatibleProductId.Bamboo,
    name: CompatibleProductName[CompatibleProductId.Bamboo],
    icon: bamboo,
    description: 'Boost software delivery with apps for builds, testing, and deployments.',
  },
  {
    id: CompatibleProductId.Crowd,
    name: CompatibleProductName[CompatibleProductId.Crowd],
    icon: crowd,
    description: 'Enhance user authentication and access control with Crowd apps.',
  },
  {
    id: CompatibleProductId.Fisheye,
    name: CompatibleProductName[CompatibleProductId.Fisheye],
    icon: fisheye,
    description:
      'Improve code visibility with apps that enhance source code tracking and insights.',
  },
  {
    id: CompatibleProductId.Crucible,
    name: CompatibleProductName[CompatibleProductId.Crucible],
    icon: crucible,
    description: 'Optimize code reviews with apps that enhance collaboration and tracking.',
  },
] as const;

export const categories = [
  {
    id: 'project-management',
    name: 'Project management',
    icon: projectManagement,
    description: 'Streamline, manage, and track project phases.',
  },
  {
    id: 'admin-tools',
    name: 'Administrative tools',
    icon: administrativeTools,
    description: `Manage users, security, and settings with apps for admins.`,
  },
  {
    id: 'content-communication',
    name: 'Content and communication',
    icon: contentAndCommunication,
    description:
      'Enhance communication with tools designed to efficiently share information and documentation among teams.',
  },
  {
    id: 'data-analytics',
    name: 'Data and analytics',
    icon: dataAndAnalytics,
    description: 'Unlock valuable insights with data visualization and analytics tools.',
  },
  {
    id: 'software-development',
    name: 'Software development',
    icon: softwareDevelopment,
    description: 'Code, collaborate, and plan across every stage of the DevOps cycle.',
  },
  {
    id: 'it-support',
    name: 'IT support and service',
    icon: itSupportAndService,
    description: 'Improve IT support with service desk, ticketing, and automation tools.',
  },
  {
    id: 'design-diagramming',
    name: 'Design and diagramming',
    icon: designAndDiagramming,
    description: 'Curate and visualize art, graphics, and digital design.',
  },
  {
    id: 'security-compliance',
    name: 'Security and compliance',
    icon: securityAndCompliance,
    description: 'Apps to secure data, enforce policies, and meet compliance standards.',
  },
  {
    id: 'hr-team',
    name: 'HR and team building',
    icon: hrAndTeamBuilding,
    description: 'Build stronger teams with HR and engagement tools.',
  },
  {
    id: 'sales-customer-relations',
    name: 'Sales and customer relations',
    icon: salesAndCustomerRelations,
    description: 'Apps for CRM, lead tracking, and customer engagement.',
  },
];

export const regulatedSectors = (
  enableIsolatedCloudChanges: boolean,
  enableFedrampChanges: boolean
) => {
  return [
    ...(enableFedrampChanges
      ? [
          {
            id: 'atlassian-government-cloud',
            name: HostingDisplayName[HostingTypesWithCloudComplianceBoundaries.GovernmentCloud],
            icon: governmentCloud,
            description:
              'Secure your mission-critical data with apps on Atlassian Government Cloud.',
          },
        ]
      : []),
    ...(enableIsolatedCloudChanges
      ? [
          {
            id: 'isolated-cloud',
            name: 'Isolated cloud',
            icon: isolatedCloud,
            description:
              'Explore apps you can deploy within your own instance of the Atlassian cloud',
          },
        ]
      : []),
  ];
};

export const resources = (enableAppification) => [
  {
    id: 'learn-and-explore',
    name: 'Learn and explore',
    links: [
      {
        text: 'About Marketplace',
        href: 'https://developer.atlassian.com/platform/marketplace/',
      },
      {
        text: 'Marketplace trust programs',
        href: 'https://www.atlassian.com/trust/marketplace?source=app-tile-badge',
      },
      {
        text: 'Marketplace partner program',
        href: 'https://developer.atlassian.com/platform/marketplace/marketplace-partner-program/',
      },
      {
        text: 'Atlassian migration program',
        href: 'https://www.atlassian.com/migration',
      },
      {
        text: enableAppification ? 'Explore Atlassian apps' : 'Explore Atlassian products',
        href: 'https://www.atlassian.com/',
      },
    ],
  },
  {
    id: 'build-and-list',
    name: 'Build and list',
    links: [
      {
        text: 'How to become a Marketplace partner',
        href: 'https://developer.atlassian.com/platform/marketplace/become-a-partner/',
      },
      {
        text: 'How to build apps',
        href: 'https://developer.atlassian.com/platform/marketplace/getting-started/',
      },
      {
        text: 'Create your own listing',
        href: 'https://developer.atlassian.com/platform/marketplace/creating-a-marketplace-listing/',
      },
      {
        text: 'Selling and licensing your app',
        href: 'https://developer.atlassian.com/platform/marketplace/selling-on-marketplace/',
      },
      {
        text: 'Access all partner resources',
        href: 'https://atlassianpartners.atlassian.net/wiki/spaces/resources/overview?mode=global',
      },
    ],
  },
  {
    id: 'help-and-support',
    name: 'Help and support',
    links: [
      {
        text: 'Frequently asked questions',
        href: 'https://www.atlassian.com/licensing/marketplace#general-faqs',
      },
      {
        text: 'Atlassian support',
        href: 'https://support.atlassian.com/',
      },
      {
        text: 'Atlassian community',
        href: 'https://community.atlassian.com/',
      },
      {
        text: 'Developer support',
        href: 'https://developer.atlassian.com/developer-guide/help-and-feedback/',
      },
      {
        text: 'System status',
        href: 'https://developer.status.atlassian.com/',
      },
    ],
  },
];

export const helpFullLinks = {
  exploreAtlassianProducts: {
    text: 'Explore Atlassian products',
    href: 'https://www.atlassian.com/software',
  },
  exploreAtlassianApps: {
    text: 'Explore Atlassian apps',
    href: 'https://www.atlassian.com/software',
  },
  learnAboutMarketplace: {
    text: 'Learn about Marketplace',
    href: 'https://developer.atlassian.com/platform/marketplace/',
  },
  getSupport: {
    text: 'Get support',
    href: 'https://support.atlassian.com/',
  },
};

export const helpMenuLinks = [
  {
    href: 'https://www.atlassian.com/licensing/marketplace#general-faqs',
    text: 'Frequently asked questions',
  },
  {
    href: 'https://support.atlassian.com/',
    text: 'Get support',
  },
  {
    href: 'https://developer.atlassian.com/platform/marketplace/getting-started/',
    text: 'Get started',
  },
];

export const dataTestIds = {
  EXPANDABLE_MENU__ITEM: 'expandable-menu__item',
  EXPANDABLE_MENU__APPS_FOR_PRODUCTS: 'expandable-menu__apps-for-products',
  EXPANDABLE_MENU__CATEGORIES: 'expandable-menu__categories',
  EXPANDABLE_MENU__RESOURCES: 'expandable-menu__resources',
  EXPANDABLE_MENU__REGULATED_SECTORS: 'expandable-menu__regulated-sectors',
  EXPANDABLE_MENU__MOBILE_VIEW: 'expandable-menu__mobile-view',
  EXPANDABLE_MENU__BLANKET: 'expandable-menu__blanket',

  USER_DROPDOWN: 'global-nav__user-dropdown',
  USER_DROPDOWN__USER_PROFILE: 'user-dropdown__user-profile',
  HELP_DROPDOWN: 'global-nav__help-dropdown',
  SEARCH: 'global-nav__search',
  PARTNER_QUICK_LINKS: 'global-nav__partner-quick-links',
};

export const FIRST_FOCUSABLE_MENU_ELEMENT = 'global-nav__menu-first-focusable-element';
export const LAST_FOCUSABLE_MENU_ELEMENT = 'global-nav__menu-last-focusable-element';

export const USER_DROPDOWN_ITEMS = {
  SWITCH_ACCOUNT: 'Switch account',
  MANAGE_ACCOUNT: 'Manage account',
  MANAGE_PARTNER_ACCOUNT: 'Manage partner account',
  PUBLISH_NEW_APP: 'Publish a new app',
  SITE_ADMIN: 'Site admin',
  THEME_TOGGLE: 'Theme',
  SIGN_OUT: 'Sign out',
};
